import { Card, CardBody } from '@chakra-ui/react';
import {
    ColumnFiltersState,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import { CustomAssetLifecycle } from '@/cip/_openapi/portfolio-optimizer';
import { TableControls } from '@/cip/widgets/Table/TableControls';
import { useClientFilterableColumns } from '@/cip/widgets/Table/useClientFilterableColumns';
import { useTableColumnSettings } from '@/cip/widgets/Table/useTableColumnSettings';

import { buildPositionLifeCycleColumns } from './buildPositionLifeCycleColumns';
import { PositionLifeCycleGrid } from './PositionLifeCycleGrid';

export const PositionLifeCycleTable = ({ report }: { report: CustomAssetLifecycle }) => {
    const { columns, defaultColumnVisibility, accentColorByColumnId, detailChildIdsByColumnId } = useMemo(
        () => buildPositionLifeCycleColumns(report.columns, report.rows),
        [report.columns, report.rows],
    );

    const filterableColumns = useClientFilterableColumns(columns);

    const { columnVisibility, setColumnVisibility, columnOrder, setColumnOrder } = useTableColumnSettings(
        'positionLifeCycle',
        { defaultColumnVisibility },
    );
    const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

    const table = useReactTable({
        data: report.rows,
        columns: filterableColumns,
        state: {
            columnVisibility,
            columnOrder,
            columnFilters,
        },
        sortDescFirst: false,
        onColumnVisibilityChange: setColumnVisibility,
        onColumnOrderChange: setColumnOrder,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <Card size="sm">
            <CardBody>
                <TableControls
                    table={table}
                    exportCsvName={`position-lifecycle-${report.pId}`}
                    onReset={() => {
                        setColumnFilters([]);
                        setColumnVisibility(defaultColumnVisibility);
                    }}
                    hideColumnSettings
                />
                <PositionLifeCycleGrid
                    table={table}
                    accentColorByColumnId={accentColorByColumnId}
                    detailChildIdsByColumnId={detailChildIdsByColumnId}
                />
            </CardBody>
        </Card>
    );
};
